import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import Avatar from '../components/Avatar';
import { apiFetch } from '../api';
import { Loading, ErrorBanner, Empty, Badge, ui, fmtDate } from '../components/adminUi';
import { formatPhone } from '../lib/phone';
import { providerRegion, providerCategories, providerImage } from '../lib/providerInfo';

const MODERATION_LABEL = { pending: 'Tasdiq kutilmoqda', approved: 'Tasdiqlangan', rejected: 'Rad etilgan' };

// Usta tafsiloti: GET /api/admin/providers/:id (404 { message: 'Usta topilmadi' } — mavjud emas).
export default function ProviderDetailPage() {
  const { id } = useParams();
  const { auth } = useAuth();
  const navigate = useNavigate();

  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setNotFound(false);
    try {
      const res = await apiFetch(`/api/admin/providers/${encodeURIComponent(id)}`, auth.token);
      if (res.status === 404) {
        setProvider(null);
        setNotFound(true);
        return;
      }
      if (!res.ok) throw new Error();
      setProvider(await res.json());
    } catch {
      setProvider(null);
      setError("Ustani yuklashda xatolik yuz berdi.");
    } finally {
      setLoading(false);
    }
  }, [auth.token, id]);

  useEffect(() => { load(); }, [load]);

  const back = (
    <button type="button" style={d.back} onClick={() => navigate('/admin/providers')}>
      ← Xizmat ko'rsatuvchilar
    </button>
  );

  if (loading) {
    return <AdminLayout>{back}<Loading /></AdminLayout>;
  }
  if (notFound) {
    return (
      <AdminLayout>
        {back}
        <Empty icon="wrench" title="Usta topilmadi" hint="Bu usta mavjud emas yoki o'chirilgan." />
      </AdminLayout>
    );
  }
  if (error || !provider) {
    return (
      <AdminLayout>
        {back}
        <ErrorBanner>
          <span style={d.errorRow}>
            <span>{error || "Ustani yuklashda xatolik yuz berdi."}</span>
            <button type="button" style={{ ...ui.btnSm, ...ui.btnRed }} onClick={load}>Qayta urinish</button>
          </span>
        </ErrorBanner>
      </AdminLayout>
    );
  }

  const name = [provider.firstName, provider.lastName].filter(Boolean).join(' ') || '—';
  const categories = providerCategories(provider);
  const ordersCount = provider.ordersCount ?? provider.orders_count;
  const status = provider.moderationStatus ?? provider.moderation_status;

  return (
    <AdminLayout>
      {back}
      <div style={d.head}>
        <Avatar src={providerImage(provider)} firstName={provider.firstName} lastName={provider.lastName} size={72} />
        <div style={{ minWidth: 0 }}>
          <h1 style={d.name}>{name}</h1>
          <div style={d.badges}>
            <Badge tone="amber">Usta</Badge>
            {status && <Badge tone={status === 'approved' ? 'green' : status === 'rejected' ? 'red' : 'amber'}>{MODERATION_LABEL[status] || status}</Badge>}
          </div>
        </div>
      </div>

      <div style={d.card}>
        <Row label="Telefon" value={formatPhone(provider.phone)} />
        <Row label="Email" value={provider.email} />
        <Row label="Hudud" value={providerRegion(provider)} />
        <Row label="Reyting" value={provider.rating != null ? `★ ${provider.rating}` : "Reyting yo'q"} />
        <Row label="Buyurtmalar soni" value={ordersCount != null ? String(ordersCount) : '0'} />
        <Row label="Ro'yxatdan o'tgan" value={fmtDate(provider.createdAt, true)} />
        <div style={d.row}>
          <span style={d.rowLabel}>Xizmat kategoriyalari</span>
          <span style={d.rowValue}>
            {categories.length === 0 ? '—' : (
              <span style={d.chips}>
                {categories.map((c) => <Badge key={c.id} tone="dark">{c.name}</Badge>)}
              </span>
            )}
          </span>
        </div>
      </div>
    </AdminLayout>
  );
}

function Row({ label, value }) {
  return (
    <div style={d.row}>
      <span style={d.rowLabel}>{label}</span>
      <span style={d.rowValue}>{value === null || value === undefined || value === '' ? '—' : value}</span>
    </div>
  );
}

const d = {
  back: {
    background: 'transparent',
    border: 'none',
    padding: '8px 0',
    marginBottom: '12px',
    fontFamily: 'var(--font-family)',
    fontSize: '14px',
    fontWeight: '700',
    color: 'var(--color-text)',
    cursor: 'pointer',
  },
  head: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', minWidth: 0 },
  name: { margin: '0 0 8px', fontSize: '24px', fontWeight: '700', color: 'var(--color-text)', wordBreak: 'break-word' },
  badges: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  card: {
    background: 'var(--color-surface-alt)',
    borderRadius: 'var(--radius-card)',
    padding: '4px 16px',
    maxWidth: '640px',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '12px 0',
    borderBottom: '1px solid var(--color-border)',
    fontSize: '14px',
  },
  rowLabel: { color: 'var(--color-text-muted)', flexShrink: 0 },
  rowValue: { color: 'var(--color-text)', fontWeight: '600', textAlign: 'right', minWidth: 0, wordBreak: 'break-word' },
  chips: { display: 'inline-flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' },
  errorRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' },
};
